import { markPasswordChangeStarted } from "../auth/auth-tab-storage.js";
import { csrfHeaders } from "../auth/session-api.js";

/**
 * Starting a self-service password change (PB-5415).
 *
 * The frontend never builds an identity-provider URL and never holds a password.
 * It asks the BFF for an action URL and navigates there; Keycloak owns the
 * screen, the credential and the password policy. The BFF decides whether this
 * caller may have a URL at all — this module carries no eligibility logic of its
 * own, only the request.
 */
export class PasswordChangeError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "PasswordChangeError";
    this.status = status;
  }
}

/**
 * Ask for the Keycloak action URL for the signed-in person's own account.
 *
 * There is no user parameter, here or on the route. `returnTo` is where the
 * browser comes back to if the person cancels; the server puts it through the
 * same allowlist as sign-in, so an unexpected value collapses to the app root
 * rather than becoming an open redirect.
 *
 * Only once the URL is in hand does the tab note that a password change has
 * started (`pb.auth.passwordChange`, PB-5461). The return page reads that note
 * so a lost sign-in ticket after a real change is not restarted as if it were
 * a sign-in. A start that fails leaves no note: the browser never left, so
 * there is nothing for the return page to be careful about.
 */
export async function startPasswordChange(returnTo: string): Promise<string> {
  const response = await fetch(
    `/api/auth/password-change?returnTo=${encodeURIComponent(returnTo)}`,
    {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json", ...csrfHeaders() },
    },
  );

  if (!response.ok) {
    throw new PasswordChangeError(
      response.status,
      `startPasswordChange: ${response.status} ${response.statusText}`,
    );
  }

  const body = (await response.json()) as { url?: unknown };
  if (typeof body.url !== "string" || body.url.length === 0) {
    throw new PasswordChangeError(
      response.status,
      "startPasswordChange: response carried no action URL",
    );
  }
  markPasswordChangeStarted();
  return body.url;
}
